// Faq.jsx - Every question, grouped, with a search box because the list is long
// and somebody with a specific worry should not have to read the other forty.

import { useMemo, useState } from "react";
import { Link } from "react-router";

import { Seo, JsonLd, breadcrumbSchema, faqSchema } from "../lib/seo.jsx";
import { FAQ_GROUPS, ALL_FAQS } from "./faqData.js";
import { Input, Empty } from "../ui/kit.jsx";
import { Loupe, Phone, Message } from "../ui/icons.jsx";
import { Reveal } from "../ui/motion.jsx";
import { PageHead, FaqList, CtaBand, MobileCta } from "./parts.jsx";

export default function Faq() {
  const [query, setQuery] = useState("");

  const groups = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return FAQ_GROUPS;
    return FAQ_GROUPS
      .map((group) => ({
        ...group,
        items: group.items.filter((item) =>
          item.question.toLowerCase().includes(term) || item.answer.toLowerCase().includes(term)
        ),
      }))
      .filter((group) => group.items.length);
  }, [query]);

  const found = groups.reduce((sum, group) => sum + group.items.length, 0);

  return (
    <>
      <Seo
        title="Questions and answers"
        path="/faq"
        description="Everything people ask about selling gold and silver by mail: what the kit costs, how insurance works, how the offer is calculated, how you get paid, and what happens if you decline."
      />
      <JsonLd data={breadcrumbSchema([{ label: "Home", to: "/" }, { label: "Questions", to: "/faq" }])} />
      <JsonLd data={faqSchema(ALL_FAQS)} />

      <PageHead
        eyebrow="Questions"
        title="Everything people ask."
        lede="Including the awkward ones. If your question is not here, call us and it probably will be tomorrow."
      >
        <div className="max-w-md">
          <Input
            placeholder="Search the questions"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            prefix={<Loupe size={16} />}
            className="pl-9"
            aria-label="Search questions"
          />
        </div>
      </PageHead>

      <div className="shell pb-14 md:pb-20">
        <div className="grid gap-10 lg:grid-cols-[220px_1fr] lg:gap-16">
          <nav className="hidden lg:block sticky top-24 self-start" aria-label="Question categories">
            <p className="eyebrow mb-3">Sections</p>
            <ul className="space-y-1.5">
              {FAQ_GROUPS.map((group) => (
                <li key={group.title}>
                  <a href={`#${group.title.replace(/\s+/g, "-").toLowerCase()}`} className="text-[14px] text-muted hover:text-ink transition-colors">
                    {group.title}
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-8 pt-6 border-t border-rule">
              <p className="hint leading-relaxed">Cannot find it?</p>
              <a href="tel:+1-401-315-4653" className="flex items-center gap-2 mt-2.5 text-sm font-semibold text-ink link">
                <Phone size={15} /> (401) 315-4653
              </a>
              <Link to="/contact" className="flex items-center gap-2 mt-2 text-sm font-semibold text-ink link">
                <Message size={15} /> Send a message
              </Link>
            </div>
          </nav>

          <div>
            {query && (
              <p className="hint mb-6">
                {found === 0 ? "Nothing matched." : `${found} ${found === 1 ? "answer" : "answers"} matched "${query}".`}
              </p>
            )}

            {groups.length === 0 ? (
              <Empty title="No answer for that yet" action={<Link to="/contact" className="btn btn-primary">Ask us directly</Link>}>
                Call us on (401) 315-4653 and we will answer it, then add it here.
              </Empty>
            ) : (
              <div className="space-y-12">
                {groups.map((group) => (
                  <Reveal as="section" key={group.title} id={group.title.replace(/\s+/g, "-").toLowerCase()} className="scroll-mt-24">
                    <h2 className="display display-md text-ink mb-4">{group.title}</h2>
                    <FaqList items={group.items} defaultOpen={query ? 0 : -1} />
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <CtaBand />
      <MobileCta />
    </>
  );
}
